// src/components/Hero.tsx
import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import AnimatedDotBackground from '@/components/ui/AnimatedDotBackground';
import CircularReveal from '@/components/ui/CircularReveal';

const Hero = () => {
  const [currentWord, setCurrentWord] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const typeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State for CircularReveal animation
  const [isRevealing, setIsRevealing] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Define service words with useMemo to prevent recreation on each render
  const serviceWords = useMemo(
    () => ['web', 'apps', 'analytics', 'ai', 'automation'],
    [],
  );

  // Get current word for reference
  const currentWordText = serviceWords[currentWord];

  // Terminal typing effect
  useEffect(() => {
    if (typeTimeoutRef.current) {
      clearTimeout(typeTimeoutRef.current);
    }

    if (isTyping) {
      // Typing effect
      if (displayText.length < currentWordText.length) {
        typeTimeoutRef.current = setTimeout(
          () => {
            setDisplayText(
              currentWordText.substring(0, displayText.length + 1),
            );
          },
          100 + Math.random() * 50,
        ); // Random delay for more natural typing
      } else {
        // Pause at the end of typing
        typeTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, 1200);
      }
    } else {
      // Backspacing effect
      if (displayText.length > 0) {
        typeTimeoutRef.current = setTimeout(() => {
          setDisplayText(displayText.substring(0, displayText.length - 1));
        }, 50);
      } else {
        // Move to next word
        typeTimeoutRef.current = setTimeout(() => {
          setCurrentWord((prev) => (prev + 1) % serviceWords.length);
          setIsTyping(true);
        }, 300);
      }
    }

    return () => {
      if (typeTimeoutRef.current) {
        clearTimeout(typeTimeoutRef.current);
      }
    };
  }, [displayText, isTyping, currentWord, serviceWords, currentWordText]);

  // Handle the button click to trigger the CircularReveal
  const handleExploreClick = () => {
    // Get button position for animation origin
    const buttonRect = buttonRef.current?.getBoundingClientRect();
    if (buttonRect) {
      // Start the reveal animation
      setIsRevealing(true);

      // Add a small vibration effect if the browser supports it
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }

      // Scroll to services section behind the scenes
      // We'll use setTimeout to give a slight delay so the animation starts first
      setTimeout(() => {
        const servicesSection = document.getElementById('services');
        if (servicesSection) {
          // Use scrollIntoView with instant behavior to avoid visible scrolling
          // The animation will hide this scroll operation from the user
          servicesSection.scrollIntoView({
            behavior: 'instant',
            block: 'start',
          });
        }
      }, 100);
    }
  };

  // When animation completes, close the reveal to show the services section
  const handleAnimationComplete = () => {
    // After the services section is fully revealed, close the animation
    setTimeout(() => {
      setIsRevealing(false);
    }, 300);
  };

  return (
    <section className="min-h-screen flex items-center pt-24 pb-16 relative overflow-hidden">
      {/* Use the reusable AnimatedDotBackground component */}
      <AnimatedDotBackground
        intensity="strong"
        speed="fast"
        fadeCenter={true}
        enhancedCorners={true}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-full">
          {/* Full width text content */}
          <div className="text-left">
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}>
              <h1 className="text-3xl font-semibold pb-2 lowercase">
                We are Hoptira
              </h1>

              <motion.div
                className="h-[2px] w-48 bg-secondary mb-8"
                initial={{ width: 0 }}
                transition={{ duration: 1.5 }}
                animate={{ width: 200 }}
              />

              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mt-6 uppercase">
                A digital agency
              </h2>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mt-4 uppercase">
                that simplifies
              </h2>

              {/* Terminal-like text container with secondary color */}
              <div className="mt-4 mb-8 flex justify-start">
                <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-secondary uppercase">
                  <span className="inline-flex items-center">
                    {displayText}
                    <span
                      className="inline-block w-0.5 h-10 sm:h-12 md:h-14 lg:h-16 xl:h-20 bg-secondary ml-1 animate-pulse"
                      aria-hidden="true"></span>
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="flex justify-start"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}>
              <Button
                ref={buttonRef}
                onClick={handleExploreClick}
                className="text-base font-semibold px-6 sm:px-8 py-5 sm:py-6 mt-6"
                size={'sm'}
                variant={'outline'}>
                Explore Services
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* CircularReveal animation that sits on top of the whole page */}
      <CircularReveal
        isOpen={isRevealing}
        onAnimationComplete={handleAnimationComplete}
        originX={
          buttonRef.current
            ? `${buttonRef.current.getBoundingClientRect().left + buttonRef.current.getBoundingClientRect().width / 2}px`
            : '50%'
        }
        originY={
          buttonRef.current
            ? `${buttonRef.current.getBoundingClientRect().top + buttonRef.current.getBoundingClientRect().height / 2}px`
            : '50%'
        }
        bgClassName="bg-primary/80"
        zIndex={50} // Higher z-index to make sure it covers everything
      />
    </section>
  );
};

export default Hero;
